import { resolveFormTenantId } from "../../utils/tenantContext";
import { useEffect, useState } from "react";

import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useApplicationFormLogic } from '../../hooks/useApplicationFormLogic';
import {
  DeleteIcon
} from "../../Icons";
import { request } from "../../utils/api";
import EvaluationAddButton from "../EvaluationNew/components/EvaluationAddButton";
import EvaluationInput from "../EvaluationNew/components/EvaluationInput";
import InputDatePicker from "../EvaluationNew/components/InputDatePicker";
import EvaluationRadioGroup from "../EvaluationNew/components/EvaluationRadioGroup";
import EvaluationFormHeader from "../EvaluationNew/components/EvaluationFormHeader";
import { toIsoDate, parseIsoDateToMDYUS } from "../../utils/dateUtils";
import {
  mergeFormDataWithStatusExtras,
  applyIsoDatesToFields,
} from "../../utils/applicationFormPayload";

import { applicationSchema, formatSSN, formatPhone, formatName } from "./application.validation";
import ApplicationFormFooter from './ApplicationFormFooter';
import CloseButton from "../../components/Application/CloseButton";
import { zodResolver } from "@hookform/resolvers/zod";
import { getStandardizedSignatures } from '../../utils/formHelpers';
import Stepper from "../../components/UI/Stepper";
import PropTypes from 'prop-types';
import CorrectionRequestAlert from "../../components/Application/CorrectionRequestAlert";

export const defaultApplicationValues = {
  tenantId: undefined,
  userId: undefined,
  applicantName: "",
  ssn: "",
  address: "",
  homePhone: "",
  cellPhone: "",
  faxNumber: "",
  email: "",
  positionAppliedFor: "",
  desiredPayRate: "",
  licenseCertificateNo: "",
  licenseCertificateState: "",
  licenseCertificateExpirationDate: "",
  driversLicenseNo: "",
  driversLicenseState: "",
  driversLicenseExpirationDate: "",
  usWorkEligibility: "", // Yes/No for UI
  firedOrResigned: "", // Yes/No for UI
  hasConvictionRecord: "", // Yes/No for UI
  applicantStatementAccepted: false,
  applicantSignature: "",
  applicantSignedAt: "",
  employmentHistories: [{ mayContactForReference: "Yes" }],
  educationHistories: [{}],
  references: [{}],
  emergencyContactName: "",
  emergencyContactAddress: "",
  emergencyContactPhone: "",
  emergencyContactRelationship: "",
  reviewedBySignature: "",
  reviewedBySignedAt: "",
  reviewedBySignedBy: ""
};

const GenericApplicationForm = ({ positionName = "RN", onClose }) => {
  const [page, setPage] = useState(1);
  const [applicationId, setApplicationId] = useState(null);

  const methods = useForm({
    resolver: zodResolver(applicationSchema),
    defaultValues: {
      ...defaultApplicationValues,
      positionAppliedFor: positionName,
    },
    mode: "onChange"
  });

  const { control, watch, reset } = methods;

  const [currentStatus, setCurrentStatus] = useState("");
  const [comments, setComments] = useState("");

  const onSubmit = async (data, flag = 'send_to_office', additionalData = {}) => {
    // Ensure flag is a string. If second arg is an event, default to 'send_to_office'
    const actualFlag = typeof flag === 'string' ? flag : 'send_to_office';

    try {
      const merged = mergeFormDataWithStatusExtras(data, additionalData);
      const payload = {
        ...merged,
        flag: actualFlag,
        usWorkEligibility: merged.usWorkEligibility === "Yes",
        firedOrResigned: merged.firedOrResigned === "Yes",
        hasConvictionRecord: merged.hasConvictionRecord === "Yes",
        ...applyIsoDatesToFields(merged, [
          "applicantSignedAt",
          "reviewedBySignedAt",
          "licenseCertificateExpirationDate",
          "driversLicenseExpirationDate",
        ]),
        employmentHistories: merged.employmentHistories?.map((h) => ({
          ...h,
          employmentFromDate: toIsoDate(h.employmentFromDate),
          employmentToDate: toIsoDate(h.employmentToDate),
        })),
        educationHistories: merged.educationHistories?.map((e) => ({
          ...e,
          attendanceStartDate: toIsoDate(e.attendanceStartDate),
          attendanceEndDate: toIsoDate(e.attendanceEndDate),
        })),
      };

      // Handle To Be Corrected comments
      const finalComments = additionalData?.comments || data?.comments || methods.getValues("comments");
      if (finalComments) {
        payload.comments = finalComments;
      }

      if (applicationId || data.id) {
        payload.applicationId = applicationId || data.id;
        payload.id = applicationId || data.id;
      }

      const stripNulls = (obj) => {
        if (Array.isArray(obj)) {
          return obj.map(v => stripNulls(v));
        } else if (obj !== null && typeof obj === 'object') {
          return Object.keys(obj).reduce((acc, key) => {
            const value = stripNulls(obj[key]);
            if (value !== null && value !== undefined && value !== "") {
              acc[key] = value;
            }
            if (value === false) {
              acc[key] = value;
            }
            return acc;
          }, {});
        }
        return obj;
      };

      const sanitizedPayload = stripNulls(payload);

      let response;
      if (applicationId || data.id) {
        response = await request(`/employment-applications/${applicationId || data.id}`, {
          method: 'PUT',
          body: JSON.stringify(sanitizedPayload)
        });
      } else {
        response = await request('/employment-applications', {
          method: 'POST',
          body: JSON.stringify(sanitizedPayload)
        });
      }

      if (response.status === 1) {
        const createdId =
          response.result?.application?.id || response.result?.id;
        if (createdId && !applicationId) {
          setApplicationId(createdId);
        }
        toast.success(response.message || "Application processed successfully!");
        closeForm()
      } else {
        toast.error(response.message || "Failed to process application!");
      }

    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error(`Failed to process application: ${error.message}`);
    }
  };

  const {
    isSignatureModalOpen,
    openSignatureModal,
    closeSignatureModal,
    signerName,
    handleStatusAction,
    handleSave,
    targetUserId,
    user: currentUserData,
    isAdmin,
    isReadOnly,
    closeForm,
    actualFromId,
    handleSignatureApply
  } = useApplicationFormLogic({
    methods,
    applicantNameField: "applicantName",
    applicantSignatureField: "applicantSignature",
    applicantSignatureDateField: "applicantSignedAt",
    positionName,
    onClose,
    onSubmit,
    currentStatus
  });

  // Explicit Fetch Logic
  useEffect(() => {
    const fetchApplicationData = async () => {
      // Must wait for currentUserData to be populated to determine role
      if (!currentUserData) return;

      // If admin, we generally need a targetUserId to know who to look up.
      // If no targetUserId as admin, we likely can't do anything (unless there's a different logic).
      if (isAdmin && !targetUserId && !applicationId) return;

      try {
        // If we don't have an ID to fetch from, and we are a user (not admin), 
        // we should assume this is a new form and autofill from profile.
        if (!actualFromId && !targetUserId && !applicationId) {
          if (currentUserData && !isAdmin) {
            // Autofill for new form
            reset({
              ...defaultApplicationValues,
              tenantId: resolveFormTenantId(currentUserData?.tenantId),
              userId: currentUserData.id,
              applicantName: `${currentUserData.firstName || ''} ${currentUserData.lastName || ''}`.trim(),
              ssn: currentUserData.ssn || "",
              address: currentUserData.address || "",
              homePhone: currentUserData.mobileNumber || "",
              cellPhone: "",
              email: currentUserData.email || "",
              positionAppliedFor: positionName,
            });
          }
          return;
        }

        let response;

        if (isAdmin) {
          if (actualFromId) {
            response = await request(`/employment-applications/${actualFromId}?userId=${targetUserId}`);
          } else {
            response = await request(`/employment-applications/${targetUserId}`);
          }
        } else {
          response = await request(`/employment-applications/${actualFromId || targetUserId}`);
        }

        let appData = null;
        if (response.status === 1) {
          if (Array.isArray(response.result)) {
            if (response.result.length > 0) appData = response.result[0];
          } else if (response.result?.application) {
            appData = response.result.application;
          } else if (response.result?.items && Array.isArray(response.result.items)) {
            // Logic from reference: find item by userId
            appData = response.result.items.find(item => item.userId === Number.parseInt(targetUserId));
          }
        }

        if (appData) {
          setApplicationId(appData.id);
          setCurrentStatus(appData.status || "");
          setComments(appData.comments || "");

          const formattedData = {
            ...defaultApplicationValues,
            ...appData,
            usWorkEligibility: appData.usWorkEligibility ? "Yes" : "No",
            firedOrResigned: appData.firedOrResigned ? "Yes" : "No",
            hasConvictionRecord: appData.hasConvictionRecord ? "Yes" : "No",
            licenseCertificateExpirationDate: parseIsoDateToMDYUS(appData.licenseCertificateExpirationDate),
            driversLicenseExpirationDate: parseIsoDateToMDYUS(appData.driversLicenseExpirationDate),
            employmentHistories: appData.employmentHistories?.length
              ? appData.employmentHistories.map(h => ({
                ...h,
                employmentFromDate: parseIsoDateToMDYUS(h.employmentFromDate),
                employmentToDate: parseIsoDateToMDYUS(h.employmentToDate),
              }))
              : [{ mayContactForReference: "Yes" }],
            educationHistories: appData.educationHistories?.length
              ? appData.educationHistories.map(e => ({
                ...e,
                attendanceStartDate: parseIsoDateToMDYUS(e.attendanceStartDate),
                attendanceEndDate: parseIsoDateToMDYUS(e.attendanceEndDate),
              }))
              : [{}],
            references: appData.references?.length ? appData.references : [{}],
            ...getStandardizedSignatures({ ...appData, applicantSignatureDate: appData.applicantStatementSignedAt }, isAdmin),
            comments: appData.comments || "",
            ssn: appData.ssn || (!isAdmin ? currentUserData.ssn : "") || ""
          };
          reset(formattedData);
        } else if (currentUserData && !isAdmin && !appData) {
          // Fallback autofill if fetch failed or returned no data for user
          reset({
            ...defaultApplicationValues,
            tenantId: resolveFormTenantId(currentUserData?.tenantId),
            userId: currentUserData.id,
            applicantName: `${currentUserData.firstName || ''} ${currentUserData.lastName || ''}`.trim(),
            ssn: currentUserData.ssn || "",
            address: currentUserData.address || "",
            homePhone: currentUserData.mobileNumber || "",
            cellPhone: currentUserData.mobileNumber || "",
            email: currentUserData.email || "",
            positionAppliedFor: positionName,
          });
        }
      } catch (error) {
        console.error("Failed to fetch application data:", error);
      }
    };

    fetchApplicationData();
  }, [targetUserId, currentUserData, reset, applicationId, isAdmin, positionName, actualFromId]);


  const { fields: employmentFields, append: appendEmployment, remove: removeEmployment } = useFieldArray({
    control,
    name: "employmentHistories"
  });

  const { fields: educationFields, append: appendEducation, remove: removeEducation } = useFieldArray({
    control,
    name: "educationHistories"
  });

  const { fields: referenceFields, append: appendReference, remove: removeReference } = useFieldArray({
    control,
    name: "references"
  });



  const handleOnNext = async () => {
    if (isReadOnly) {
      setPage(p => Math.min(4, p + 1));
      return;
    }
    const fieldsToValidate = getStepFields(page);
    const result = await methods.trigger(fieldsToValidate);
    if (result) setPage(p => Math.min(4, p + 1));
  };



  const renderPage1 = () => (
    <div className="space-y-6 animate-fade-in">
      <h2 className="text-2xl font-bold text-gray-800 border-b pb-2">
        Personal Information
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <EvaluationInput
          name="applicantName"
          label="Name"
          placeholder="e.g. Jane Doe"
          isRequired
          onInput={(e) => { e.target.value = formatName(e.target.value); }}
        />
        <EvaluationInput
          name="ssn"
          label="Social Security Number"
          placeholder="XXX-XX-XXXX"
          isRequired
          maxLength={11}
          onInput={(e) => {
            e.target.value = formatSSN(e.target.value);
          }}
        />
        <EvaluationInput
          name="address"
          label="Address"
          className="md:col-span-2"
        />
        {/* <EvaluationInput name="city" label="City" /> 
                <EvaluationInput name="state" label="State" /> */}
        {/* <EvaluationInput name="zipCode" label="Zip Code" /> */}
        <EvaluationInput
          name="homePhone"
          label="Home Phone"
          placeholder="(XXX)XXX-XXXX"
          maxLength={13}
          isRequired
          onInput={(e) => { e.target.value = formatPhone(e.target.value); }}
        />
        <EvaluationInput
          name="cellPhone"
          label="Cell/Other Phone"
          placeholder="(XXX)XXX-XXXX"
          maxLength={13}
          onInput={(e) => { e.target.value = formatPhone(e.target.value); }}
        />
        <EvaluationInput
          name="faxNumber"
          label="Fax"
          placeholder="(XXX)XXX-XXXX"
          maxLength={13}
          onInput={(e) => { e.target.value = formatPhone(e.target.value); }}
        />
        <EvaluationInput
          name="email"
          label="Email Address"
          type="email"
          isRequired
        />
        <EvaluationInput
          name="positionAppliedFor"
          label="Position Applied For"
          readOnly
        />
        <EvaluationInput
          name="desiredPayRate"
          label="Desired Rate of Pay:"
        />
      </div>

      <h2 className="text-2xl font-bold text-gray-800 border-b pb-2 mt-8">
        License & Certification
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <EvaluationInput name="licenseCertificateNo" label="License/Cert No." />
        <EvaluationInput name="licenseCertificateState" label="State" />
        <InputDatePicker
          formatSeparator="/"
          name="licenseCertificateExpirationDate"
          label="Expiration Date"
          max="9999-12-31"
        />

        <EvaluationInput name="driversLicenseNo" label="Driver's License No." />
        <EvaluationInput name="driversLicenseState" label="State" />
        <InputDatePicker
          formatSeparator="/"
          name="driversLicenseExpirationDate"
          label="Expiration Date"
          max="9999-12-31"
        />
      </div>

      <h2 className="text-2xl font-bold text-gray-800 border-b pb-2 mt-8">
        Emergency Contact
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <EvaluationInput
          name="emergencyContactName"
          label="Name"
          onInput={(e) => { e.target.value = formatName(e.target.value); }}
        />
        <EvaluationInput
          name="emergencyContactRelationship"
          label="Relationship"

        />
        <EvaluationInput
          name="emergencyContactPhone"
          label="Phone"
          placeholder="(XXX)XXX-XXXX"
          maxLength={13}
          onInput={(e) => { e.target.value = formatPhone(e.target.value); }}
        />
        <EvaluationInput
          name="emergencyContactAddress"
          label="Address"
          className="md:col-span-2"

        />
      </div>
    </div>
  );

  const renderPage2 = () => (
    <div className="space-y-6 animate-fade-in">
      <h2 className="text-2xl font-bold text-gray-800 border-b pb-2">
        Eligibility Questions
      </h2>

      <div className="space-y-4">
        <EvaluationRadioGroup
          name="usWorkEligibility"
          isRequired
          label="To qualify for employment, you must be either (a) a citizen of the United States of America or (b) a registered alien with government permission to work in this country. Does either statement (a) or (b) describe your status as a resident of this country?"
          options={[
            { label: "Yes", value: "Yes" },
            { label: "No", value: "No" },
          ]}
        />
        <EvaluationRadioGroup
          isRequired
          name="firedOrResigned"
          label="Have you ever been fired or asked to resign?"
          options={[
            { label: "Yes", value: "Yes" },
            { label: "No", value: "No" },
          ]}
        />
        <EvaluationRadioGroup
          isRequired
          name="hasConvictionRecord"
          label="Have you ever been convicted, fined (excluding minor traffic offenses), placed on probation or given a suspended sentence in any court, including Medicare fraud?"
          options={[
            { label: "Yes", value: "Yes" },
            { label: "No", value: "No" },
          ]}
        />
      </div>

      <h2 className="text-2xl font-bold text-gray-800 border-b pb-2 mt-8">
        Education History
      </h2>
      {educationFields.map((field, index) => (
        <div
          key={field.id}
          className="p-4 bg-gray-50 rounded-lg border border-gray-200 relative"
        >
          {index > 0 && (
            <button
              type="button"
              onClick={() => removeEducation(index)}
              className="absolute top-2 right-2 text-red-500 hover:text-red-700 transition-colors p-1"
              title="Remove School"
            >
              <DeleteIcon className="w-5 h-5" />
            </button>
          )}
          <h4 className="font-semibold mb-2">School #{index + 1}</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <EvaluationInput
              name={`educationHistories.${index}.schoolNameAddress`}
              label="Name and address of colleges/schools attended (starting with your most recent)"
              className="md:col-span-2"
              isRequired
            />
            <EvaluationInput
              name={`educationHistories.${index}.majorSubject`}
              label="Major Subject or Course"
              isRequired
            />
            <EvaluationInput
              name={`educationHistories.${index}.degreeCertificate`}
              label="Degree or Certificate Received"
              isRequired
            />
            <div className="grid grid-cols-2 gap-4">
              <InputDatePicker
                formatSeparator="/"
                name={`educationHistories.${index}.attendanceStartDate`}
                label="From"
                isRequired
              />
              <InputDatePicker
                formatSeparator="/"
                name={`educationHistories.${index}.attendanceEndDate`}
                label="To"
                isRequired
              />
            </div>
          </div>
        </div>
      ))}
      <EvaluationAddButton onClick={() => appendEducation({})}>
        + Add School
      </EvaluationAddButton>
    </div>
  );

  const renderPage3 = () => (
    <div className="space-y-6 animate-fade-in">
      <h2 className="text-2xl font-bold text-gray-800 border-b pb-2">
        Employment History
      </h2>
      {employmentFields.map((field, index) => (
        <div
          key={field.id}
          className="p-4 bg-gray-50 rounded-lg border border-gray-200 relative"
        >
          <button
            type="button"
            onClick={() => removeEmployment(index)}
            className="absolute top-2 right-2 text-red-500 hover:text-red-700 transition-colors p-1"
            title="Remove Employer"
          >
            <DeleteIcon className="w-5 h-5" />
          </button>
          <h4 className="font-semibold mb-2">Employer #{index + 1}</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <EvaluationInput
              name={`employmentHistories.${index}.employer`}
              label="Employer Name"
            />
            <EvaluationInput
              name={`employmentHistories.${index}.address`}
              label="Address"
            />
            <EvaluationInput
              name={`employmentHistories.${index}.city`}
              label="City"
            />
            <EvaluationInput
              name={`employmentHistories.${index}.state`}
              label="State"
            />
            <EvaluationInput
              name={`employmentHistories.${index}.zipCode`}
              label="Zip Code"
            />
            <EvaluationInput
              name={`employmentHistories.${index}.telephoneNo`}
              label="Telephone"
              placeholder="(XXX)XXX-XXXX"
              maxLength={13}
              onInput={(e) => { e.target.value = formatPhone(e.target.value); }}
            />
            <EvaluationInput
              name={`employmentHistories.${index}.immediateSupervisorTitle`}
              label="Immediate Supervisor Title"
            />
            <EvaluationInput
              name={`employmentHistories.${index}.salaryWages`}
              label="Salary Wages"
            />
            <EvaluationInput
              name={`employmentHistories.${index}.jobTitleResponsibility`}
              label="Job Title Responsibility"
              className="md:col-span-2"
            />

            <div className="grid grid-cols-2 gap-4 md:col-span-2">
              <InputDatePicker
                formatSeparator="/"
                name={`employmentHistories.${index}.employmentFromDate`}
                label="Duration of Employment - From"
              />
              <InputDatePicker
                formatSeparator="/"
                name={`employmentHistories.${index}.employmentToDate`}
                label="Duration of Employment - To"
              />
            </div>

            <EvaluationRadioGroup
              name={`employmentHistories.${index}.mayContactForReference`}
              label="May we contact for reference?"
              options={[
                { label: "Yes", value: "Yes" },
                { label: "No", value: "No" },
                { label: "Later", value: "Later" },
              ]}
            />
          </div>
        </div>
      ))}
      <EvaluationAddButton onClick={() => appendEmployment({ mayContactForReference: "Yes" })}>
        + Add Employer
      </EvaluationAddButton>

      <h2 className="text-2xl font-bold text-gray-800 border-b pb-2 mt-8">
        References
      </h2>
      <p className="text-sm text-gray-700 mb-4">
        List business/work references that are not related to you and are not
        previous supervisors. If not applicable, list school or personal
        references who are not related to you. (Optional)
      </p>
      {referenceFields.map((field, index) => (
        <div
          key={field.id}
          className="p-4 bg-gray-50 rounded-lg border border-gray-200 relative"
        >
          <button
            type="button"
            onClick={() => removeReference(index)}
            className="absolute top-2 right-2 text-red-500 hover:text-red-700 transition-colors p-1"
            title="Remove Reference"
          >
            <DeleteIcon className="w-5 h-5" />
          </button>
          <h4 className="font-semibold mb-2">Reference #{index + 1}</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <EvaluationInput
              name={`references.${index}.referenceName`}
              label="Name"
              onInput={(e) => { e.target.value = formatName(e.target.value); }}
            />
            <EvaluationInput
              name={`references.${index}.referencePhone`}
              label="Phone"
              placeholder="(XXX)XXX-XXXX"
              maxLength={13}
              onInput={(e) => { e.target.value = formatPhone(e.target.value); }}
            />
            <EvaluationInput
              name={`references.${index}.referenceAddress`}
              label="Address (if known)"
            />
            <EvaluationInput
              name={`references.${index}.referenceTitle`}
              label="Title"
            />
            <EvaluationInput
              name={`references.${index}.yearsKnown`}
              label="Years Known"
              type="number"
            />
          </div>
        </div>
      ))}
      <EvaluationAddButton onClick={() => appendReference({})}>
        + Add Reference
      </EvaluationAddButton>
    </div>
  );

  const renderPage4 = () => (
    <div className="space-y-6 animate-fade-in">
      <h2 className="text-2xl font-bold text-gray-800 border-b pb-2">
        Applicant Statement
      </h2>
      <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 text-sm text-gray-700 space-y-4">
        <p>
          I certify that all information provided by me in this application is
          true and complete, and I understand that if any false, incomplete, or
          misrepresented information is discovered, my application may be
          rejected and, if I am employed, my employment may be terminated. I
          understand that any offer of employment is contingent upon my passing
          a job-related physical examination and/or satisfactory background
          check.
        </p>
        <p>
          I authorize, without reservation, any party or agency contacted by
          this employer to furnish the above-mentioned information. I hereby
          release, discharge and hold harmless, to the extent permitted by law,
          the employer, its representatives, employees or agents, both
          individually and collectively, from any and all liability for damages
          which may result from the furnishing and/or use of such information. I
          waive any right or claim I may have regarding the employer seeking,
          gathering and using lawful information that is non-defamatory in a
          truthful manner in the employment process and all other persons,
          corporations or organizations for furnishing such information about
          me.
        </p>
        <p>
          I understand that this employer does not unlawfully discriminate in
          employment and no question on this application is used for the purpose
          of limiting or eliminating any applicant from consideration for
          employment on any basis prohibited by applicable local, state or
          federal law.
        </p>
        <p>
          I understand that this application is current for 30 days. At the
          conclusion of this time, if I have not heard from the employer and
          still wish to be considered for employment, it will be necessary to
          reapply and fill out a new application.
        </p>
        <p>
          If I am hired, I understand that I am free to resign at any time, with
          or without cause and without prior notice, and the employer reserves
          the same right to terminate my employment at any time, with or without
          cause and without prior notice. This application does not constitute
          an agreement or contract for employment for any specified period or
          definite duration. I understand that no supervisor or representative
          of the employer is authorized to make any assurances to the contrary
          and that no implied oral or written agreements contrary to the
          foregoing express language are valid unless they are in writing and
          signed by the employer's president.
        </p>
        <p>
          I understand that if I am hired, I will be required to provide proof
          of identity and legal authorization to work in the United States and
          to complete the required employment eligibility verification (I-9
          Form) upon hire.
        </p>
        <p>
          I understand that any false, incomplete, or misrepresented information
          given in my application or interview(s) may result in elimination from
          consideration for employment or, if I am employed, my immediate
          discharge.
        </p>
      </div>

    </div>
  );

  const getStepFields = (step) => {
    switch (step) {
      case 1:
        return [
          "applicantName",
          "ssn",
          "address",
          "city",
          "state",
          "zipCode",
          "homePhone",
          "cellPhone",
          "email",
          "positionAppliedFor",
          "desiredPayRate",
          "licenseCertificateNo",
          "licenseCertificateState",
          "licenseCertificateExpirationDate",
          "driversLicenseNo",
          "driversLicenseState",
          "driversLicenseExpirationDate",
          "emergencyContactName",
          "emergencyContactRelationship",
          "emergencyContactPhone",
          "emergencyContactAddress",
        ];
      case 2:
        // educationHistories is an array, we trigger the field name itself.
        // internal item validation happens via generic schema, but preventing empty arrays if required is different.
        // For now, trigger validation on the top level fields.
        return [
          "usWorkEligibility",
          "firedOrResigned",
          "hasConvictionRecord",
          "educationHistories",
        ];
      case 3:
        return ["employmentHistories", "references"];
      case 4:
        return ["applicantStatementAccepted", "applicantSignature", "applicantSignedAt"];
      default:
        return [];
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8 font-sans">
      <CloseButton onClose={closeForm} />
      <div className="max-w-5xl mx-auto bg-white shadow-xl overflow-hidden">
        {/* Header Section - Clean & Functional */}
        <EvaluationFormHeader
          title="Employment Application"
          subtitle={`${positionName || "RN"} Position`}
          email={watch("email")}
        />

        {/* Progress Steps (Simplified) */}
        <Stepper
          currentStep={page}
          steps={["Personal", "Education", "History", "Submit"]}
        />

        <div className="p-8 md:p-12">
          <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit((data) => onSubmit(data, 'send_to_office'))}>

              <fieldset disabled={isReadOnly} className={`border-none p-0 m-0 transition-all duration-300 ${isReadOnly ? 'opacity-60 bg-gray-50/50 grayscale-[0.2]' : ''}`}>
                <div className="min-h-[300px] transition-all duration-300 ease-in-out">
                  {/* Comments Section (To Be Corrected) */}
                  <CorrectionRequestAlert status={currentStatus} comments={comments} />
                  {page === 1 && renderPage1()}
                  {page === 2 && renderPage2()}
                  {page === 3 && renderPage3()}
                  {page === 4 && renderPage4()}
                </div>
              </fieldset>


              <ApplicationFormFooter
                page={page}
                setPage={setPage}
                totalPages={4}
                methods={methods}
                onSave={handleSave}
                onSubmit={(data, status) => onSubmit(data, status)}
                onStatusUpdate={handleStatusAction}
                onNext={handleOnNext}
                isSignatureModalOpen={isSignatureModalOpen}
                openSignatureModal={openSignatureModal}
                closeSignatureModal={closeSignatureModal}
                handleSignatureApply={handleSignatureApply}
                signerName={signerName}
                showCertification={true}
                requireSignatures={true}
                showSignatureSections={true}
                applicantName={watch("applicantName") || ""}
                applicantSignature={watch("applicantSignature") || ""}
                applicantSignedAt={watch("applicantSignedAt") || ""}
                reviewedBySignedBy={watch("reviewedBySignedBy") || signerName}
                reviewedBySignature={watch("reviewedBySignature") || ""}
                reviewedBySignedAt={watch("reviewedBySignedAt") || ""}
                isReadOnly={isReadOnly}
                onClose={closeForm}
                currentStatus={currentStatus}
              />
            </form>


          </FormProvider>
        </div>

        {/* Footer */}

      </div>
    </div>
  );
};

export default GenericApplicationForm;

GenericApplicationForm.propTypes = {
  positionName: PropTypes.string,
  onClose: PropTypes.func
};
